import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getProductIngoingOutgoingList,
  addIngoingOutgoingToProduct,
  updateProductIngoingOutgoing,
  deleteProductIngoingOutgoing,
} from "../../../../../../apis/Product/ProductIngoingOutgoingDelivery";
import {
  getTransportDeliveriesData,
  getTransportCountryData,
} from "../../../../../../apis/Dropdown";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const ProdDeliveriesTable = ({ productId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [unitMaterials, setUnitMaterials] = useState({});
  const [dropdownCountries, setDropdownCountries] = useState([]);
  const [dropdownUsedFor, setDropdownUsedFor] = useState([]);

  useEffect(() => {
    getTransportDeliveriesData({
      setDropdownData: setDropdownUsedFor,
    });
    getTransportCountryData({
      setDropdownData: setDropdownCountries,
    });
  }, []);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProductIngoingOutgoingList({
      productId,
      setObjects: setData,
    });
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (data.length > 0) {
      const newUnitMaterials = {};
      data.forEach((row) => {
        const materialValue =
          row["means_transportation"]?.value || row.means_transportation;
        if (materialValue) {
          const selectedMaterial = dropdownUsedFor.find(
            (option) => option?.value === materialValue
          );
          if (selectedMaterial) {
            newUnitMaterials[row.id] = selectedMaterial.unit;
          }
        }
      });
      setUnitMaterials(newUnitMaterials);
    }
  }, [data, dropdownUsedFor]);

  const dynamicPlaceholders = useMemo(
    () => ({
      means_transportation: {
        targetField: "total_weight_delivery",
        getPlaceholder: (
          selectedValue,
          dropdownData,
          rowData,
          rowId = "new"
        ) => {
          const selectedMaterial = dropdownData?.find(
            (option) => option?.value === selectedValue
          );
          if (selectedMaterial) {
            // For existing rows
            if (rowId !== "new") {
              setUnitMaterials((prev) => ({
                ...prev,
                [rowId]: selectedMaterial.unit,
              }));
            } else {
              setUnitMaterials((prev) => ({
                ...prev,
                new: selectedMaterial.unit, // Use a special key for new rows
              }));
            }
          }
        },
        dropdownDataKey: "means_transportation",
      },
    }),
    []
  );
  const handleEdit = useCallback(
    (editedItem) => {
      updateProductIngoingOutgoing({
        productId,
        componentId: editedItem.id,
        ...editedItem,
        means_transportation: editedItem.means_transportation.value,
        country_arrival: editedItem.country_arrival.value,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [productId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addIngoingOutgoingToProduct({
        productId,
        ...newItem,
        means_transportation: newItem.means_transportation.value,
        country_arrival: newItem.country_arrival.value,
        callback: fetchData,
        use_swal: true,
      });
    },
    [productId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProductIngoingOutgoing({
        productId,
        componentId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "means_transportation",
        header: `${t("MEANS")}*`,
        type: "dropdown",
        dropdownData: dropdownUsedFor,
        disableSortBy: true,
      },
      {
        accessor: "transport_distance",
        header: `${t("TRANSPORT_DISTANCE")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("TIP_DISTANCE_TRANSPORT"),
        unit: "km",
      },
      {
        accessor: "manuel",
        header: t("TRANSPORT_MANUEL"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "120px",
        unit: "kgCO₂e/tonnes-km",
        tipHeader: "Tip",
        tipBody: t("TIP_MANUEL_TRANSPORT"),
        optional: true,
      },
      {
        accessor: "country_arrival",
        header: `${t("TRANSPORT_COUNTRY_ARRIVAL")}*`,
        type: "dropdown",
        dropdownData: dropdownCountries,
        disableSortBy: true,
        width: "110px",
        optional: true,
      },
      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "total_weight_delivery",
        header: (props) => {
          const rowId = props.row?.original?.id;
          let finalUnit = "";
          if (unitMaterials[rowId]) {
            unitMaterials[rowId].includes(".km")
              ? (finalUnit = unitMaterials[rowId].slice(0, 3))
              : (finalUnit = unitMaterials[rowId].slice(0, 2));
          }

          return `${t("FUNC_UNIT_DELIVERY")} (${finalUnit || "xx"})*`;
        },
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "150px",
        unit: (rowId) => {
          let finalUnit = "";
          if (unitMaterials[rowId]) {
            unitMaterials[rowId].includes(".km")
              ? (finalUnit = unitMaterials[rowId].slice(0, 3))
              : (finalUnit = unitMaterials[rowId].slice(0, 2));
          }
          return `${finalUnit || "xx"}`;
        },
      },
      {
        accessor: "manuel_distribution",
        header: t("STORAGE_DISTRIBUTOR"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "150px",
        unit: "tCO₂e",
        tipHeader: "Tip",
        tipBody: t("STORAGE_DISTRIBUTOR_TIP"),
        optional: true,
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "120px",
      },
    ],
    [dropdownUsedFor, t, unitMaterials, dropdownCountries]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      means_transportation: dropdownUsedFor,
      country_arrival: dropdownCountries,
    }),
    [dropdownUsedFor, dropdownCountries]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default ProdDeliveriesTable;
